import { RefObject, useCallback, useEffect, useRef, useState } from "react";

type ScrollListHint<T extends HTMLElement> = {
  ref: RefObject<T | null>;
  showHint: boolean;
  onScroll: () => void;
  scrollDown: () => void;
};

const SCROLL_TOP_THRESHOLD = 8;

// Shows a "scroll for more" hint only while the list overflows and the user is
// still at the top; call `onScroll` on the scroll container and re-runs when
// `contentKey` changes (e.g. item count).
export const useScrollListHint = <T extends HTMLElement>(
  contentKey: unknown
): ScrollListHint<T> => {
  const ref = useRef<T>(null);
  const [showHint, setShowHint] = useState(false);

  const onScroll = useCallback((): void => {
    const el = ref.current;
    if (!el) return;

    const canScroll = el.scrollHeight - el.clientHeight > SCROLL_TOP_THRESHOLD;
    const isAtTop = el.scrollTop <= SCROLL_TOP_THRESHOLD;
    setShowHint(canScroll && isAtTop);
  }, []);

  useEffect(() => {
    onScroll();

    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(onScroll);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [onScroll, contentKey]);

  const scrollDown = useCallback((): void => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  }, []);

  return { ref, showHint, onScroll, scrollDown };
};
